import React from 'react'
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { type GroupBase, type StylesConfig } from 'react-select'

import type {} from 'react-select/base'
import { type SelectProps } from './Select'
// This import is necessary for module augmentation.
// It allows us to extend the 'Props' interface in the 'react-select/base' module
// and add our custom properties to it.
// See https://react-select.com/typescript#custom-select-props

declare module 'react-select/base' {
  export interface Props<
    Option,
    // the disable is needed to prevent lint warning. This type comes straight from the react-select docs
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    IsMulti extends boolean,
    // the disable is needed to prevent lint warning. This type comes straight from the react-select docs
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Group extends GroupBase<Option>,
  > {
    error?: boolean
    isFocused?: boolean
    onMenuInputFocus?: () => void
    optionKeyName?: keyof Option
    labelKeyName?: keyof Option
    noBorder?: boolean
    searchBarProps?: SelectProps<Option>['searchBarProps']
    footerButtonProps?: SelectProps<Option>['footerButtonProps']
    isOffscreenRight: boolean
    menuRef: RefObject<HTMLDivElement>
    menuWidth?: number
    hasMore?: boolean
    getData?: () => void
  }
}

export type SelectGroupType<OptionType> = {
  label: string
  options: OptionType[]
  rightLabel?: string
}

type GetStylesType<OptionType> = StylesConfig<
  OptionType,
  boolean,
  GroupBase<OptionType>
>

export const getStyles = <OptionType,>(): GetStylesType<OptionType> => {
  return {
    control: (provided, state) => ({
      ...provided,
      boxShadow: 'none',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      borderColor: state.isFocused
        ? 'var(--blue)'
        : state.isDisabled
          ? 'var(--light-gray)'
          : state.selectProps.error
            ? 'var(--red)'
            : 'var(--medium-purple)',
      ...(state.selectProps.noBorder ? { border: 'none' } : {}),
      height: '40px', // matches multiple selection height
      fontSize: '12px',
      backgroundColor: state.isDisabled
        ? 'var(--faint-gray)'
        : provided.backgroundColor,
      '&:hover': {
        borderColor: state.isFocused
          ? 'var(--blue)'
          : state.isDisabled
            ? 'var(--light-gray)'
            : state.selectProps.error
              ? 'var(--red)'
              : 'var(--medium-purple)',
      },
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: state.isDisabled
        ? 'var(--medium-gray)'
        : state.selectProps.menuIsOpen
          ? 'var(--dark-purple)'
          : 'var(--purple)',
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? 'var(--medium-gray)' : 'var(--dark-purple)',
      fontSize: '12px',
      backgroundColor: state.isFocused
        ? 'var(--light-blue)'
        : state.isSelected
          ? 'var(--white)'
          : provided.backgroundColor,
      '&:hover': {
        backgroundColor: 'var(--light-blue)',
      },
      padding: '0px',
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? 'var(--medium-gray)' : 'var(--dark-purple)',
      fontSize: '12px',
      ...(state.selectProps.noBorder ? { marginLeft: 0 } : {}),
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      ...(state.selectProps.noBorder ? { paddingLeft: 0 } : {}),
    }),
    menu: (provided, state) => ({
      ...provided,
      marginTop: state.placement === 'bottom' ? '12px' : provided.marginTop,
      marginBottom: state.placement === 'top' ? '12px' : provided.marginBottom,
      width: 'max-content',
      minWidth: state.selectProps.menuWidth
        ? state.selectProps.menuWidth
        : '100%',
      // the below styles create the triangle above / below the dropdown
      '&:before': {
        content: '""',
        width: '0',
        height: '0',
        borderLeft: '11px solid rgba(0,0,0,0)',
        borderRight: '11px solid rgba(0,0,0,0)',
        borderBottom:
          state.placement === 'bottom'
            ? '11px solid var(--medium-purple)'
            : undefined,
        borderTop:
          state.placement === 'top'
            ? '11px solid var(--medium-purple)'
            : undefined,
        position: 'absolute',
        ...(state.selectProps.isOffscreenRight
          ? { right: 15 } // Position of the `medium-purple` "border" triangle when the dropdown is aligned to the right. Needs to be one px less than the "after" triangle.
          : state.selectProps.menuWidth
            ? {
                left: 15,
              }
            : { right: 'calc(50% - 11px)' }), // This calculation will center the triangle on the menu. Needs to be one px less than the "after" triangle.
        top: state.placement === 'bottom' ? '-11px' : undefined,
        bottom: state.placement === 'top' ? '-11px' : undefined,
      },
      '&:after': {
        content: '""',
        width: '0',
        height: '0',
        borderLeft: '10px solid rgba(0,0,0,0)',
        borderRight: '10px solid rgba(0,0,0,0)',
        borderBottom:
          state.placement === 'bottom' ? '10px solid var(--white)' : undefined,
        borderTop:
          state.placement === 'top' ? '10px solid var(--white)' : undefined,
        position: 'absolute',
        ...(state.selectProps.isOffscreenRight
          ? { right: 16 } // Position of the `white` "inner" triangle when the dropdown is aligned to the right. Needs to be one px more than the "before" triangle.
          : state.selectProps.menuWidth
            ? {
                left: 16,
              }
            : { right: 'calc(50% - 10px)' }), // This calculation will center the triangle on the menu. Needs to be one px more than the "before" triangle.
        top: state.placement === 'bottom' ? '-10px' : undefined,
        bottom: state.placement === 'top' ? '-10px' : undefined,
      },
      ...(state.selectProps.isOffscreenRight ? { right: 0 } : {}),
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10000, // 1 higher than tippy z-index
      position: 'fixed',
    }),
    menuList: (provided) => ({
      ...provided,
      paddingTop: '8px',
      paddingBottom: '8px',
    }),
    groupHeading: (provided) => ({
      ...provided,
      paddingLeft: '8px',
      paddingRight: '8px',
    }),
  }
}

type ClickOutsideOfSelectProps = {
  setIsFocused: (val: boolean) => void
  setInputValue: (val: string) => void
}

export const useClickOutOfSelect = ({
  setIsFocused,
  setInputValue,
}: ClickOutsideOfSelectProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  // Close the menu if the user clicks outside of the select
  // See https://github.com/JedWatson/react-select/issues/3111#issuecomment-470911304
  const onDomClick = useCallback(
    (e: Event) => {
      const menu = containerRef.current?.querySelector('.select__menu')

      if (
        !containerRef.current?.contains(e.target as Node) ||
        !menu ||
        !menu.contains(e.target as Node)
      ) {
        setIsFocused(false)
        setInputValue('')
      }
    },
    [setInputValue, setIsFocused],
  )

  useEffect(() => {
    document.addEventListener('mousedown', onDomClick)

    return () => {
      document.removeEventListener('mousedown', onDomClick)
    }
  }, [onDomClick])

  return containerRef
}

type OptionElement<OptionType> = React.ReactElement<{
  data: OptionType
  [key: string]: unknown
}>

type GroupElement<OptionType> = React.ReactElement<{
  children: OptionElement<OptionType>[]
  [key: string]: unknown
}>

export type OptionsArray<OptionType> = Array<
  OptionElement<OptionType> | GroupElement<OptionType>
>

// Function to extract React elements from an array of options and groups
export const extractElementsFromOptionsArray = <OptionType,>(
  elements: OptionsArray<OptionType>,
): OptionElement<OptionType>[] => {
  // Check if elements is not an array - this likely means it's a "no options" message component
  if (!Array.isArray(elements)) {
    return elements
  }

  // If it is an array, proceed with the reduction operation
  return elements.reduce(
    (
      acc: OptionElement<OptionType>[],
      element: OptionElement<OptionType> | GroupElement<OptionType>,
    ) => {
      // Check if the element is a group by looking for the 'children' property
      if (element.props && Array.isArray(element.props.children)) {
        // extract the children property from element to prevent rendering children multiple times
        const { children, ...rest } = element.props
        // Insert the group header element followed by its children (the options)
        acc.push(
          React.cloneElement(element as React.ReactElement<any>, {
            ...rest,
            children: [],
          }) as OptionElement<OptionType>,
        )
        acc = acc.concat(children as OptionElement<OptionType>[])
      } else {
        // It's a single option element, so add it to the accumulator
        acc.push(element as OptionElement<OptionType>)
      }
      return acc
    },
    [] as OptionElement<OptionType>[],
  )
}

export const useBottomEdgeProximityDetector = (
  parentRef: React.RefObject<HTMLElement | null>,
  menuHeight: number,
): boolean => {
  const [isNearBottom, setIsNearBottom] = useState(false)

  useEffect(() => {
    const checkEdgeProximity = () => {
      if (!parentRef.current) return

      const parentRect = parentRef.current.getBoundingClientRect(),
        viewportHeight = window.innerHeight,
        isNearBottom = viewportHeight - parentRect.bottom < menuHeight
      setIsNearBottom(isNearBottom)
    }

    checkEdgeProximity()

    const handleResize = () => {
      checkEdgeProximity()
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [menuHeight, parentRef])

  return isNearBottom
}

export const useRightEdgeDetector = (
  menuRef: React.RefObject<HTMLDivElement | null>,
  isFocused: boolean,
) => {
  const [isOffscreen, setIsOffscreen] = useState(false),
    menuObserver = useRef<IntersectionObserver | undefined>(undefined)

  useEffect(() => {
    const observeOnscreen = (entries: IntersectionObserverEntry[]) => {
      const { boundingClientRect, intersectionRect } = entries[0]
      const isOffscreenRight = boundingClientRect.width > intersectionRect.width
      setIsOffscreen(isOffscreenRight)
    }

    setTimeout(() => {
      const menuList = menuRef.current
      menuObserver.current = new IntersectionObserver(observeOnscreen)
      menuList && menuObserver.current.observe(menuList)
    }, 1)

    return () => {
      // Stop observing when the component unmounts or the menuRef changes
      if (menuObserver.current) {
        menuObserver.current.disconnect()
        setIsOffscreen(false)
      }
    }
  }, [menuRef, isFocused])

  return isOffscreen
}
